import type { ColumnFiltersState } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';
import request from 'lib/request';
import { api } from 'lib/request/api';
import type {
  RatioRecord,
  VolumeRecord,
  WellProductionDataSources,
  WellProductionDataSourcesResponse,
} from 'types/api-responses';
import type { DcaWorkspaceWellsQueryPayload } from 'types/dca-workspace-wells-api/requests';
import type {
  DcaWorkspaceWellApi,
  DcaWorkspaceWellsMeta,
  DcaWorkspaceWellsQueryResponse,
  DcaWorkspaceWellsCardFilters,
} from 'types/dca-workspace-wells-api/responses';
import { IHS_ONLY_SOURCES } from 'components/production-source-dropdown';
import { buildWorkspaceWellsQueryPayload } from './build-workspace-wells-query-payload';
import {
  errorMessageFromResponse,
  fetchBulkProduction,
  fetchBulkForecasts,
  fetchBulkTypeCurves,
} from './utils';
import {
  VisibleWellSpec,
  PerWellChartData,
  Production,
  TypeCurveVolumes,
} from './types';

const QUERY_PATH = '/queries/dca_workspace_wells';

const SEARCH_DEBOUNCE_MS = 300;
const FILTERED_UWIS_PAGE_SIZE = 1_000;

/** The filter scope the wells table is currently showing. */
export type WorkspaceWellsFilter = {
  text: string;
  columnFilters: ColumnFiltersState;
  uwis?: string[];
  excludeUwis?: string[];
  wellsMissingForecast?: boolean;
  wellsInEarlyTime?: boolean;
  wellsUpdated?: boolean;
};

type FetchFilteredUwisParams = WorkspaceWellsFilter & {
  workspaceId: number;
  dcaScenarioId?: number | null;
};

/**
 * Fetches every UWI matching the current table filter (all pages).
 *
 * @param {FetchFilteredUwisParams} params - Same filter scope as the wells table query.
 * @param {AbortController} [abortController] - Optional abort controller.
 * @return {Promise<string[]>} UWIs in the filtered set.
 */
export async function fetchAllFilteredUwis(
  params: FetchFilteredUwisParams,
  abortController?: AbortController
): Promise<string[]> {
  const controller = abortController ?? new AbortController();
  const collected: string[] = [];
  let page = 1;
  let totalCount = Infinity;

  while (collected.length < totalCount) {
    const payload = buildWorkspaceWellsQueryPayload({
      workspaceId: params.workspaceId,
      dcaScenarioId: params.dcaScenarioId,
      page,
      perPage: FILTERED_UWIS_PAGE_SIZE,
      text: params.text,
      columnFilters: params.columnFilters,
      uwis: params.uwis,
      excludeUwis: params.excludeUwis,
      wellsMissingForecast: params.wellsMissingForecast,
      wellsInEarlyTime: params.wellsInEarlyTime,
      wellsUpdated: params.wellsUpdated,
    });

    const response = await request.post<
      DcaWorkspaceWellsQueryResponse,
      unknown
    >(QUERY_PATH, { query: payload }, controller);

    if (response.type === 'abort') return collected;
    if (response.type === 'error') {
      throw new Error(errorMessageFromResponse(response.data));
    }
    if (response.type !== 'success') {
      throw new Error(response.message);
    }

    const data = response.data;
    totalCount = data.totalCount ?? 0;
    const pageUwis = (data.results ?? []).map((w) => w.uwi);
    if (pageUwis.length === 0) break;

    collected.push(...pageUwis);
    page += 1;
  }

  return collected;
}

export type DcaWorkspaceWellsHook = ReturnType<typeof useDcaWorkspaceWells>;

export const useDcaWorkspaceWells = (options: {
  workspaceId: number;
  dcaScenarioId?: number | null;
}) => {
  const { workspaceId, dcaScenarioId } = options;

  const [wells, setWells] = useState<DcaWorkspaceWellApi[]>([]);

  const [meta, setMeta] = useState<DcaWorkspaceWellsMeta>({
    total: 0,
    page: 1,
    perPage: 100,
  });
  const [cardFilters, setCardFilters] = useState<DcaWorkspaceWellsCardFilters>({
    totalWells: 0,
    wellsMissingForecast: 0,
    wellsInEarlyTime: 0,
    wellsUpdated: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [uwis, setUwis] = useState<string[] | undefined>();
  const [excludeUwis, setExcludeUwis] = useState<string[] | undefined>();
  const [wellsMissingForecast, setWellsMissingForecast] = useState<
    boolean | undefined
  >();
  const [wellsInEarlyTime, setWellsInEarlyTime] = useState<
    boolean | undefined
  >();
  const [wellsUpdated, setWellsUpdated] = useState<boolean | undefined>();

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(globalFilter),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [globalFilter]);

  const fetchWells = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const payload: DcaWorkspaceWellsQueryPayload =
      buildWorkspaceWellsQueryPayload({
        workspaceId,
        dcaScenarioId,
        page,
        perPage: pageSize,
        text: debouncedSearch,
        columnFilters,
        uwis,
        excludeUwis,
        wellsMissingForecast,
        wellsInEarlyTime,
        wellsUpdated,
      });

    try {
      const response = await request.post<
        DcaWorkspaceWellsQueryResponse,
        unknown
      >(QUERY_PATH, { query: payload }, controller);

      if (controller.signal.aborted) return;
      if (response.type === 'abort') return;
      if (response.type === 'fatal') {
        setError(response.message ?? 'Request failed');
        setWells([]);
        setMeta({ total: 0, page: 1, perPage: pageSize });
        return;
      }
      if (response.type === 'error') {
        setError(errorMessageFromResponse(response.data));
        setWells([]);
        setMeta({ total: 0, page: 1, perPage: pageSize });
        return;
      }
      if (response.type !== 'success') return;
      const data = response.data;

      setWells(data.results ?? []);
      setMeta({
        total: data.totalCount ?? 0,
        page: data.page ?? 1,
        perPage: data.pageSize ?? pageSize,
      });
      if (data.cardFilters) {
        setCardFilters(data.cardFilters);
      }
    } catch {
      if (!controller.signal.aborted) {
        setError('Failed to load wells');
        setWells([]);
        setMeta({ total: 0, page: 1, perPage: pageSize });
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [
    workspaceId,
    dcaScenarioId,
    page,
    pageSize,
    debouncedSearch,
    columnFilters,
    uwis,
    excludeUwis,
    wellsMissingForecast,
    wellsInEarlyTime,
    wellsUpdated,
  ]);

  useEffect(() => {
    if (!dcaScenarioId || !workspaceId) return;
    void fetchWells();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchWells, dcaScenarioId, workspaceId]);

  const onFilterChange = useCallback((filters: ColumnFiltersState) => {
    setColumnFilters(filters);
    setPage(1);
  }, []);

  const onGlobalSearchChange = useCallback((search: string) => {
    setGlobalFilter(search);
    setPage(1);
  }, []);

  const onPageChange = useCallback((p: number, ps: number) => {
    setPage(p);
    setPageSize(ps);
  }, []);

  const onSelectionFilterChange = useCallback(
    (filter: { uwis?: string[]; excludeUwis?: string[] }) => {
      setUwis(filter.uwis);
      setExcludeUwis(filter.excludeUwis);
      setPage(1);
    },
    []
  );

  const resetFilters = useCallback(() => {
    setColumnFilters([]);
    setGlobalFilter('');
    setDebouncedSearch('');
    setUwis(undefined);
    setExcludeUwis(undefined);
    setWellsMissingForecast(undefined);
    setWellsInEarlyTime(undefined);
    setWellsUpdated(undefined);
    setPage(1);
  }, []);

  const onMissingForecastChange = useCallback((value: boolean | undefined) => {
    setWellsMissingForecast(value);
    setPage(1);
  }, []);

  const onEarlyTimeWellChange = useCallback((value: boolean | undefined) => {
    setWellsInEarlyTime(value);
    setPage(1);
  }, []);

  const onWellsUpdatedChange = useCallback((value: boolean | undefined) => {
    setWellsUpdated(value);
    setPage(1);
  }, []);

  return {
    wells,
    meta,
    cardFilters,
    loading,
    error,
    page,
    pageSize,
    fetchWells,
    onFilterChange,
    onGlobalSearchChange,
    onPageChange,
    onSelectionFilterChange,
    resetFilters,
    onMissingForecastChange,
    onEarlyTimeWellChange,
    onWellsUpdatedChange,
    columnFilters,
    globalFilter,
    uwis,
    excludeUwis,
    wellsMissingForecast,
    wellsInEarlyTime,
    wellsUpdated,
  };
};

const VISIBILITY_DEBOUNCE_MS = 150;

interface CacheEntry {
  data: PerWellChartData;
  forecastId: string;
}

export const EMPTY_PRODUCTION: Production = {
  volume: [],
  cumulative: [],
  ratio: [],
  materialBalanceTime: [],
};

const LOADING_WELL_DATA: PerWellChartData = {
  production: EMPTY_PRODUCTION,
  forecasts: [],
  typeCurveVolumes: null,
  isLoading: true,
};

export const useBulkChartVolumes = (
  visibleSpecs: VisibleWellSpec[],
  dcaScenarioId: string | null
): Record<string, PerWellChartData> => {
  const cacheRef = useRef<Record<string, CacheEntry>>({});
  const batchAcRef = useRef<AbortController | null>(null);
  const [dataMap, setDataMap] = useState<Record<string, PerWellChartData>>({});

  const prevScenarioRef = useRef(dcaScenarioId);

  if (prevScenarioRef.current !== dcaScenarioId) {
    cacheRef.current = {};
    prevScenarioRef.current = dcaScenarioId;
  }

  const visibleKey = useMemo(
    () => visibleSpecs.map((s) => `${s.uwi}:${s.dcaForecastId}`).join(','),
    [visibleSpecs]
  );

  useEffect(() => {
    if (!visibleSpecs.length) {
      setDataMap({});
      return;
    }

    const nextMap: Record<string, PerWellChartData> = {};
    const uncachedSpecs: VisibleWellSpec[] = [];

    for (const spec of visibleSpecs) {
      const entry = cacheRef.current[spec.uwi];
      if (
        entry &&
        !entry.data.isLoading &&
        entry.forecastId === spec.dcaForecastId
      ) {
        nextMap[spec.uwi] = entry.data;
      } else {
        nextMap[spec.uwi] = LOADING_WELL_DATA;
        uncachedSpecs.push(spec);
      }
    }

    setDataMap(nextMap);

    if (uncachedSpecs.length === 0) return;

    const timer = setTimeout(() => {
      batchAcRef.current?.abort();
      const ac = new AbortController();
      batchAcRef.current = ac;

      const uwis = uncachedSpecs.map((s) => s.uwi);

      void (async () => {
        const [prodR, fcR, tcR] = await Promise.allSettled([
          fetchBulkProduction(uwis, ac, dcaScenarioId),
          fetchBulkForecasts(uncachedSpecs, ac),
          dcaScenarioId
            ? fetchBulkTypeCurves(uwis, dcaScenarioId, ac)
            : Promise.resolve({} as Record<string, TypeCurveVolumes | null>),
        ]);

        if (ac.signal.aborted) return;

        // Rejections will be suppressed by Promise.allSettled, which is very confusing.
        // Log them to help with debugging issues in the future.
        if (prodR.status === 'rejected')
          console.error('DCA bulk production fetch failed:', prodR.reason);
        if (fcR.status === 'rejected')
          console.error('DCA bulk forecast fetch failed:', fcR.reason);
        if (tcR.status === 'rejected')
          console.error('DCA bulk type curve fetch failed:', tcR.reason);

        const prod = prodR.status === 'fulfilled' ? prodR.value : {};
        const fc = fcR.status === 'fulfilled' ? fcR.value : {};
        const tc = tcR.status === 'fulfilled' ? tcR.value : {};

        for (const spec of uncachedSpecs) {
          cacheRef.current[spec.uwi] = {
            forecastId: spec.dcaForecastId,
            data: {
              production: prod[spec.uwi] ?? EMPTY_PRODUCTION,
              forecasts: fc[spec.uwi] ?? [],
              typeCurveVolumes: tc[spec.uwi] ?? null,
              isLoading: false,
            },
          };
        }

        setDataMap((prev) => {
          const updated = { ...prev };
          for (const spec of visibleSpecs) {
            const entry = cacheRef.current[spec.uwi];
            if (entry) updated[spec.uwi] = entry.data;
          }
          return updated;
        });
      })();
    }, VISIBILITY_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, dcaScenarioId]);

  useEffect(
    () => () => {
      batchAcRef.current?.abort();
    },
    []
  );

  return dataMap;
};

/** No wells' daily production, kept stable between renders. */
const NO_DAILY_PRODUCTION: Record<string, Production> = {};

/** One version of a well's daily production, with the series the request asks for. */
interface DailyProductionVersion {
  productionVolumeVersionId: string;
  volumes: VolumeRecord[];
  cumulatives?: VolumeRecord[];
  ratios?: RatioRecord[];
  mbts?: VolumeRecord[];
}

/** /queries/reservoir/daily_production's response: each well's versions, keyed by uwi. */
interface DailyProductionResponse {
  results: Record<string, DailyProductionVersion[]>[];
}

/**
 * Fetches the current daily production of each well, one row per day, with
 * each day's running totals, ratios and material balance time. A well with no
 * daily production gets empty series.
 *
 * @param {string[]} uwis - The wells.
 * @param {AbortSignal} signal - Cancels the request.
 * @return {Promise<Record<string, Production>>} Each well's daily production.
 */
const fetchDailyProduction = async (uwis: string[], signal: AbortSignal) => {
  const response = await api
    .post('queries/reservoir/daily_production', {
      json: {
        query: {
          dailyProduction: [{ uwis, versionIds: ['current'] }],
          responseConfig: {
            granularity: 'daily',
            includeCumulativeProduction: true,
            includeRatiosProduction: true,
            includeMbtProduction: true,
          },
        },
      },
      signal,
    })
    .json<DailyProductionResponse>();

  const versionsByUwi: Record<string, DailyProductionVersion[]> =
    Object.fromEntries(response.results.flatMap(Object.entries));
  return Object.fromEntries(
    uwis.map((uwi): [string, Production] => {
      const current = versionsByUwi[uwi]?.[0];
      return [
        uwi,
        {
          volume: current?.volumes ?? [],
          cumulative: current?.cumulatives ?? [],
          ratio: current?.ratios ?? [],
          materialBalanceTime: current?.mbts ?? [],
        },
      ];
    })
  );
};

/**
 * Daily production for the grid's Daily display of wells whose source is
 * daily. Each well is fetched once and kept, so scrolling only fetches the
 * wells that come into view for the first time.
 *
 * @param {string[]} uwis - The wells to show.
 * @return {Record<string, Production>} The daily production of each given well that has loaded.
 */
export const useBulkDailyProduction = (uwis: string[]) => {
  const [cache, setCache] = useState<Record<string, Production>>({});
  const uwisKey = useMemo(() => [...uwis].sort().join(','), [uwis]);

  useEffect(() => {
    const missing = uwisKey.split(',').filter((uwi) => uwi && !(uwi in cache));
    if (missing.length === 0) return;

    const ac = new AbortController();
    fetchDailyProduction(missing, ac.signal)
      .then((production) => setCache((prev) => ({ ...prev, ...production })))
      .catch((error: unknown) => {
        if (!ac.signal.aborted)
          console.error('DCA daily production fetch failed:', error);
      });
    return () => ac.abort();
  }, [uwisKey, cache]);

  return useMemo(() => {
    const loaded = uwis.filter((uwi) => uwi in cache);
    if (loaded.length === 0) return NO_DAILY_PRODUCTION;
    return Object.fromEntries(loaded.map((uwi) => [uwi, cache[uwi]]));
  }, [uwis, cache]);
};

/**
 * The production data sources a well has production for, and the one the DCA
 * scenario's hierarchy picks for it. For the DCA editor, which starts on the
 * source a batch regression in that scenario would read.
 *
 * A failed lookup is retried twice. If it still fails, the editor falls back
 * to IHS, and `loadFailed` is true so the caller can say so.
 *
 * @param {string} uwi - The well; nothing is fetched without one.
 * @param {number | null | undefined} dcaScenarioId - The scenario, null for
 *   none, or undefined while it is still being chosen (nothing is fetched).
 * @return {{ sources: WellProductionDataSources | undefined, loadFailed: boolean, retry: () => void, isFetching: boolean }}
 *   The sources (undefined while they load), whether the lookup failed, a way to ask again, and
 *   whether a lookup is running.
 */
export const useProductionDataSources = (
  uwi: string,
  dcaScenarioId: number | null | undefined
) => {
  const { data, isError, isFetching, refetch } = useQuery(
    queryOptions({
      queryKey: ['dca-workspace-production-data-sources', uwi, dcaScenarioId],
      queryFn: async ({ signal }): Promise<WellProductionDataSources> => {
        const response = await api
          .post('queries/reservoir/production_data_sources', {
            json: {
              query: {
                uwis: [uwi],
                ...(typeof dcaScenarioId === 'number' && { dcaScenarioId }),
              },
            },
            signal,
          })
          .json<{ results: WellProductionDataSourcesResponse[] }>();
        const well = response.results.find((result) => result.uwi === uwi);
        return well
          ? {
              availableSources: well.availableSources,
              defaultSource: well.defaultSource,
            }
          : IHS_ONLY_SOURCES;
      },
      enabled: Boolean(uwi) && dcaScenarioId !== undefined,
      retry: 2,
      retryDelay: (attempt) => (attempt + 1) * 500,
    })
  );
  const retry = useCallback(() => void refetch(), [refetch]);

  return {
    sources: isError ? IHS_ONLY_SOURCES : data,
    loadFailed: isError,
    retry,
    isFetching,
  };
};
